import type { ReactElement } from 'react';
import type { Device } from '#~/device/index.js';
import { Button } from '@studio/design-system/button';
import { Icon } from '@studio/design-system/icon';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';

/** The canvas explains a retained camera's lock without giving members billing controls. */
export const LockedCameraNotice = ({
  deviceId,
  availableCamera,
  onSelect
}: {
  readonly deviceId: Device['id'];
  readonly availableCamera: Pick<Device, 'id' | 'name'> | undefined;
  readonly onSelect: (id: Device['id']) => void;
}): ReactElement => (
  <div
    className='flex h-full min-h-0 flex-col overflow-y-auto rounded-inherit bg-base-300 p-16 text-center'
    aria-label='Camera locked'
    role='status'
    {...DataSTag(Sel.StandardTrialNotice, 'locked-camera', deviceId)}
  >
    {/* Auto margins keep the explanation reachable in short, scrollable previews. */}
    <div className='m-auto w-full max-w-[420px]'>
      <Icon icon='lock' aria-hidden='true' className='text-2xl text-muted' />
      <h2 className='mt-12 font-demibold text-light'>Camera locked</h2>
      <p className='mt-8 text-sm text-muted'>
        This Studio's Standard trial has ended. Free includes one camera.
      </p>
      <p className='mt-8 text-sm text-muted'>
        Your camera settings are saved. The Studio owner can upgrade to restore this camera.
      </p>
      {availableCamera && (
        <Button
          variant='secondary'
          className='focus-visible:ring-primary/50 mt-16 max-w-full whitespace-normal focus-visible:ring-2'
          {...STagNew(Sel.StandardTrialNotice, 'view-available-camera', deviceId)}
          onClick={(event) => {
            // A multi-view tile's selection handler must not reselect the locked source.
            event.stopPropagation();
            onSelect(availableCamera.id);
          }}
        >
          View {availableCamera.name || 'available camera'}
        </Button>
      )}
    </div>
  </div>
);
